import { observer } from 'mobx-react-lite';
import { useResolve } from '../use';
import { TOKEN_FrontDeskStore } from './FrontDeskStore';
import type { Court } from '@/domain/session';

// Two taps per player, and the second one is the court: tap register is the court tap
// (docs/roster.md, docs/user-journeys.md). The number comes back and goes on the player's arm with
// a marker, so it is rendered as large as the screen allows and nothing competes with it.
//
// Legible beats pretty here on purpose. The goal puts anything visual beyond legible out of scope.

const COURT_TONE: Record<Court, string> = {
  white: 'bg-neutral-200 text-neutral-900',
  red: 'bg-rose-600 text-white',
  purple: 'bg-purple-600 text-white',
  gold: 'bg-amber-500 text-neutral-900',
};

export const FrontDeskView = observer(function FrontDeskView() {
  const desk = useResolve(TOKEN_FrontDeskStore);

  return (
    <div className="flex flex-1 flex-col gap-6 p-6">
      <Arm />
      <NumberInput />
      {desk.connectionMessage !== undefined && (
        <p
          role="alert"
          className="rounded-lg bg-rose-950 p-4 text-center text-lg text-rose-200 ring-1 ring-rose-800"
        >
          {desk.connectionMessage}
        </p>
      )}
      {desk.lastError !== undefined && (
        <p
          role="alert"
          className="rounded-lg bg-amber-950 p-4 text-center text-lg text-amber-100 ring-1 ring-amber-800"
        >
          {desk.lastError}
        </p>
      )}
      <CourtButtons />
    </div>
  );
});

// The number the desk writes on an arm. Nothing else on this screen is allowed to be this big.
const Arm = observer(function Arm() {
  const desk = useResolve(TOKEN_FrontDeskStore);

  return (
    <section className="flex flex-col items-center justify-center rounded-xl bg-neutral-900 py-10 ring-1 ring-neutral-800">
      <span className="text-sm tracking-widest text-neutral-500 uppercase">
        write this on the arm
      </span>
      <span className="text-[7rem] leading-none font-bold tabular-nums">
        {desk.lastAssigned ?? '—'}
      </span>
    </section>
  );
});

// The number off the club's spreadsheet, typed. Only on a pre-allocated session; on a sequential
// one there is nothing to type and the reducer owns the numbering (docs/roster.md).
//
// `inputMode="numeric"` plus `pattern="[0-9]*"` is what brings up the numeric keypad rather than the
// full keyboard on a phone, which is the whole ask: a desk is doing this a hundred times with one
// thumb. It is `type="text"` and not `type="number"` on purpose. A number input on mobile still
// lets a thumb enter `e`, `+` and `.`, it silently reports an empty string for anything it thinks
// is invalid so the store cannot sanitise what it never receives, and it grows spinner arrows
// nobody at a desk wants to hit. The store does the sanitising instead, which is where it can be
// tested.
//
// VISUAL, and therefore not verifiable here: whether the keypad actually comes up, and whether the
// field is big enough to hit. session-v2/visual-residual.md says how to get to this screen.
const NumberInput = observer(function NumberInput() {
  const desk = useResolve(TOKEN_FrontDeskStore);

  if (!desk.needsNumber) return null;

  return (
    <section className="flex flex-col gap-2">
      <label htmlFor="player-number" className="text-sm tracking-widest text-neutral-500 uppercase">
        player number
      </label>
      <input
        id="player-number"
        value={desk.numberDraft}
        onChange={(event) => desk.setNumberDraft(event.target.value)}
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        autoComplete="off"
        // Dead only while the desk is CLOSED. A field that takes a number while offline collects it
        // for a tap that cannot happen.
        //
        // NOT while a write is in flight, though P2's triage asked for that. Two things changed
        // under it. `clearDraft` now clears the number the write CONSUMED rather than whatever is
        // in the box, so typing the next player during the round trip is already safe and already
        // preserved, which was the whole reason for the busy half. And disabling a FOCUSED input is
        // how Chromium blurs it: measured in the production bundle, focus landed on BODY after
        // every registration, which on a phone dismisses the numeric keypad and costs the desk a
        // tap per player. The two halves of that measurement are this binding and the court
        // button's own `disabled`, and both had to go for focus to survive.
        disabled={!desk.connectionOk}
        // The desk types one of these per player and never edits the last one, so the field starts
        // empty and takes focus on its own.
        autoFocus
        placeholder="—"
        aria-describedby={desk.draftProblem === undefined ? undefined : 'player-number-problem'}
        aria-invalid={desk.draftProblem !== undefined}
        className="rounded-xl bg-neutral-900 py-6 text-center text-6xl font-bold tabular-nums ring-1 ring-neutral-700 focus:ring-2 focus:ring-sky-500 focus:outline-none"
      />
      {desk.draftProblem !== undefined && (
        <p
          id="player-number-problem"
          // `status`, not `alert`. This is derived from every keystroke, and `alert` is
          // `aria-live="assertive"`, so a screen reader would be interrupted mid-typing each time
          // the draft crossed into a number somebody holds. The `aria-describedby` association
          // above is what actually gets this read out, and it is already correct. The two BANNERS
          // below keep `alert`: they report something that already happened.
          role="status"
          className="rounded-lg bg-amber-950 p-3 text-center text-lg text-amber-100 ring-1 ring-amber-800"
        >
          {desk.draftProblem}
        </p>
      )}
    </section>
  );
});

// One button per court, each carrying its own live count. The count is what the desk balances off
// and it is shared with every other desk in the building, so it never leaves the screen.
const CourtButtons = observer(function CourtButtons() {
  const desk = useResolve(TOKEN_FrontDeskStore);

  return (
    <section className="grid flex-1 grid-cols-2 gap-4">
      {desk.courts.map((court) => (
        <button
          key={court}
          // The count is in the accessible name, not only in the pixels: it is the number the desk
          // balances off, and it is what an e2e journey addresses the button by.
          aria-label={`${court}, ${desk.courtCounts[court]} on court`}
          disabled={!desk.canRegister}
          // Keeps the number field focused across a registration. The button disables itself the
          // instant it is tapped (`canRegister` goes false while the write is in flight), and
          // Chromium blurs a focused element when it becomes disabled, so focus was landing on
          // BODY after every player. On a phone that dismisses the numeric keypad and costs the
          // desk a tap per player, which over a hundred players is the whole ergonomic argument for
          // the keypad in the first place. Measured in the production bundle in real Chromium, not
          // reasoned about. Preventing the default on mousedown stops focus leaving the input at
          // all, so the disable has no focused element to blur, and it touches neither `disabled`
          // nor the store's own reentrancy guard.
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => void desk.tapCourt(court)}
          className={`flex flex-col items-center justify-center gap-2 rounded-xl text-2xl font-semibold disabled:opacity-30 ${COURT_TONE[court]}`}
        >
          <span className="uppercase">{court}</span>
          <span className="text-5xl tabular-nums">{desk.courtCounts[court]}</span>
        </button>
      ))}
    </section>
  );
});
